// A report model, on screen — the header block and its section tables.
//
// Presentation only. It is the SAME model core/utils/xlsx writes and
// ui/shared/printReport prints, so what is checked here is what comes out of
// the printer and the spreadsheet; a preview built separately from the thing it
// previews is not a preview.
//
// Shared by the User Access list dialog and the Installed Devices page, which
// is why it lives here rather than beside either of them.

import React from 'react';
import {
  Box, Typography, Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material';
import { groupSections } from './reportLayout';

// 'Online' green, 'Offline' red — the words carry it, the colour only speeds it
// up. `tone` is the report's own vocabulary; see the note in printReport.
const TONE_COLOR = { ok: 'success.main', bad: 'error.main' };

const cellSx = (col, value) => ({
  py: 0.25,
  px: 0.75,
  fontSize: 11.5,
  whiteSpace: col.mono ? 'nowrap' : 'normal',
  fontFamily: col.mono ? 'monospace' : undefined,
  textAlign: col.align === 'right' ? 'right' : 'left',
  color: TONE_COLOR[col.tone && col.tone(value)] || undefined,
});

/*
 * ⚠️ `tableLayout: fixed` PLUS THE COLGROUP, exactly as the printed sheet does
 * it. Without them MUI sizes every column by its content, so the preview and
 * the paper laid the same rows out in different proportions — and one long
 * device name shoved "Unique Number" and "Status" out of line. `wordBreak` is
 * the other half: a fixed column does not grow for an unbreakable value, it
 * overflows into its neighbour.
 */
function Section({ section }) {
  const cols = section.columns || [];
  const rows = section.rows || [];
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
        <Typography sx={{ fontWeight: 700, fontSize: 12.5 }}>{section.title}</Typography>
        <Typography color="text.secondary" sx={{ fontSize: 10.5 }}>
          {section.subtitle ? `${section.subtitle} · ` : ''}
          {`${rows.length} row${rows.length === 1 ? '' : 's'}`}
        </Typography>
      </Box>
      {rows.length === 0 ? (
        <Typography color="text.secondary" sx={{ fontSize: 11, fontStyle: 'italic', py: 0.5 }}>
          {section.empty || 'Nothing here.'}
        </Typography>
      ) : (
        <Table
          size="small"
          sx={{
            tableLayout: 'fixed',
            '& td, & th': { borderColor: 'divider', wordBreak: 'break-word' },
          }}
        >
          <colgroup>
            {cols.map((c) => (
              <col key={c.key} style={c.width ? { width: c.width } : undefined} />
            ))}
          </colgroup>
          <TableHead>
            <TableRow>
              {cols.map((c) => (
                <TableCell
                  key={c.key}
                  sx={{
                    ...cellSx(c),
                    fontFamily: undefined,
                    color: 'text.secondary',
                    fontWeight: 700,
                    // Sentence case and wrappable — see the note in
                    // printReport: uppercase cost "Sr. No." more width than its
                    // own column had, and it overflowed into the next one.
                    fontSize: 10.5,
                    whiteSpace: 'normal',
                    borderBottom: 2,
                    borderColor: 'text.primary',
                  }}
                >
                  {c.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, i) => (
              // The row key is the section-local index: a unique number is NOT
              // unique across a report (a device can appear in more than one
              // section) and neither is a name.
              // eslint-disable-next-line react/no-array-index-key
              <TableRow
                key={`${section.title}-${i}`}
                hover
                sx={{ '&:nth-of-type(even)': { bgcolor: 'action.hover' } }}
              >
                {cols.map((c) => (
                  <TableCell key={c.key} sx={cellSx(c, r[c.key])}>{r[c.key]}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}

/** The header block — what the printed sheet carries above the rule. */
export function ReportMeta({ report }) {
  if (!report) return null;
  // A GRID, not a flex row — the same as the printed band. Content-sized items
  // left the labels at ragged positions, which is what read as "not aligned".
  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
      gap: 1,
    }}
    >
      {(report.meta || []).map((m) => (
        <Box key={m.label} sx={{ minWidth: 0 }}>
          <Typography
            color="text.secondary"
            sx={{
              fontSize: 9, lineHeight: 1.2, textTransform: 'uppercase', letterSpacing: '.08em',
            }}
          >
            {m.label}
          </Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 700, lineHeight: 1.3 }}>
            {m.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export default function ReportView({ report }) {
  if (!report) return null;
  // Grouped exactly the way the printed sheet groups them — a preview laid out
  // differently from the paper is not a preview. See ui/shared/reportLayout.
  return (
    <>
      {groupSections(report.sections).map((g, gi) => (
        <Box
          // eslint-disable-next-line react/no-array-index-key
          key={`g${gi}`}
          sx={g.compact ? {
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
            alignItems: 'start',
          } : undefined}
        >
          {g.sections.map((s, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <Section key={`${s.title}-${i}`} section={s} />
          ))}
        </Box>
      ))}
    </>
  );
}
